const YT_ID_RE = /^[A-Za-z0-9_-]{6,32}$/;
const MIN_POSITION_SECONDS = 5;
const MIN_VOD_DURATION_SECONDS = 60;
const END_THRESHOLD_SECONDS = 5;
const MIN_DELTA_SECONDS = 2;
const MAX_POSITION_SECONDS = 86400;

export function storageKey(youtubeVideoId) {
  return `bp_progress_${youtubeVideoId}`;
}

export function isValidYoutubeVideoId(youtubeVideoId) {
  return typeof youtubeVideoId === 'string' && YT_ID_RE.test(youtubeVideoId);
}

export function readLocalProgress(youtubeVideoId) {
  if (!isValidYoutubeVideoId(youtubeVideoId)) return null;
  try {
    const raw = localStorage.getItem(storageKey(youtubeVideoId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    const position = Math.floor(Number(data?.position));
    const updatedAt = Number(data?.updatedAt);
    if (!Number.isFinite(position) || position < 0) return null;
    if (!Number.isFinite(updatedAt) || updatedAt <= 0) return null;
    return { position, updatedAt };
  } catch {
    return null;
  }
}

export function writeLocalProgress(youtubeVideoId, positionSeconds) {
  if (!isValidYoutubeVideoId(youtubeVideoId)) return false;
  const position = Math.floor(Number(positionSeconds));
  if (!Number.isFinite(position) || position < 0 || position > MAX_POSITION_SECONDS) {
    return false;
  }
  try {
    localStorage.setItem(
      storageKey(youtubeVideoId),
      JSON.stringify({ position, updatedAt: Date.now() })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Decide whether a position is worth persisting.
 * Short VOD clips (typical BigPlays highlights) are skipped.
 * Lives are persisted (DVR resume) — callers must resume via seekTo after
 * the player is healthy, never via iframe start= (that blacks out live embeds).
 */
export function shouldPersist(positionSeconds, durationSeconds, { isLive = false } = {}) {
  const position = Math.floor(Number(positionSeconds));
  if (!Number.isFinite(position) || position < MIN_POSITION_SECONDS) return false;
  if (position > MAX_POSITION_SECONDS) return false;

  // Live duration grows / is unknown — persist absolute stream position.
  if (isLive) return true;

  const duration = Number(durationSeconds);
  const isLiveLike = !Number.isFinite(duration) || duration <= 0 || duration === Infinity;
  // Unknown duration without an explicit isLive flag: do not guess.
  if (isLiveLike) return false;

  if (duration < MIN_VOD_DURATION_SECONDS) return false;
  if (position > duration - END_THRESHOLD_SECONDS) return false;
  return true;
}

export function shouldWriteServer(positionSeconds, lastSavedPosition) {
  const position = Math.floor(Number(positionSeconds));
  const last = Math.floor(Number(lastSavedPosition));
  if (!Number.isFinite(position)) return false;
  if (!Number.isFinite(last)) return true;
  return Math.abs(position - last) >= MIN_DELTA_SECONDS;
}

/** Clips must not overwrite continue-watching progress. */
export function shouldSkipProgressWrite({ isClipPlayback = false } = {}) {
  return !!isClipPlayback;
}

export function pickNewer(local, server) {
  if (!local && !server) return null;
  if (!local) return server;
  if (!server) return local;

  const localTs = Number(local.updatedAt) || 0;
  const serverTs = Number(server.updatedAt) || 0;
  return serverTs > localTs ? server : local;
}

function parseServerUpdatedAt(updatedAt) {
  if (!updatedAt) return 0;
  const ts = new Date(updatedAt).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

export async function fetchServerProgress(youtubeVideoId) {
  if (!isValidYoutubeVideoId(youtubeVideoId)) return null;
  try {
    const res = await fetch(`/api/video-progress/${encodeURIComponent(youtubeVideoId)}`, {
      credentials: 'include'
    });
    if (res.status === 401) return null;
    if (!res.ok) return null;
    const data = await res.json();
    const position = Math.floor(Number(data?.positionSeconds));
    if (!Number.isFinite(position) || position < 0) {
      return { position: 0, updatedAt: 0 };
    }
    return {
      position,
      updatedAt: parseServerUpdatedAt(data.updatedAt)
    };
  } catch {
    return null;
  }
}

export async function saveServerProgress(youtubeVideoId, positionSeconds) {
  if (!isValidYoutubeVideoId(youtubeVideoId)) return false;
  const position = Math.floor(Number(positionSeconds));
  if (!Number.isFinite(position) || position < 0 || position > MAX_POSITION_SECONDS) {
    return false;
  }
  try {
    const res = await fetch('/api/video-progress', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        youtubeVideoId,
        positionSeconds: position
      }),
      keepalive: true
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function saveServerProgressBeacon(youtubeVideoId, positionSeconds) {
  if (!isValidYoutubeVideoId(youtubeVideoId)) return false;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }
  const position = Math.floor(Number(positionSeconds));
  if (!Number.isFinite(position) || position < 0 || position > MAX_POSITION_SECONDS) {
    return false;
  }
  try {
    const blob = new Blob(
      [JSON.stringify({ youtubeVideoId, positionSeconds: position })],
      { type: 'application/json' }
    );
    return navigator.sendBeacon('/api/video-progress', blob);
  } catch {
    return false;
  }
}

export function getPlayerPosition(player) {
  if (!player?.getCurrentTime) return null;
  try {
    const t = player.getCurrentTime();
    if (!Number.isFinite(t) || t < 0) return null;
    return Math.floor(t);
  } catch {
    return null;
  }
}

export function getPlayerDuration(player) {
  if (!player?.getDuration) return null;
  try {
    return player.getDuration();
  } catch {
    return null;
  }
}

/** Raw YouTube clock plus live-head hints the IFrame API keeps on playerInfo. */
export function readPlayerClock(player) {
  let position = null;
  let duration = null;
  let playerState = null;
  let looksLive = false;
  if (!player) return { position, duration, playerState, looksLive };
  try {
    if (typeof player.getCurrentTime === 'function') {
      const t = player.getCurrentTime();
      if (Number.isFinite(t) && t >= 0) position = t;
    }
  } catch { /* ignore */ }
  try {
    if (typeof player.getDuration === 'function') {
      const d = player.getDuration();
      if (Number.isFinite(d) && d >= 0) duration = d;
    }
  } catch { /* ignore */ }
  try {
    if (typeof player.getPlayerState === 'function') {
      playerState = player.getPlayerState();
    }
  } catch { /* ignore */ }
  try {
    if (typeof player.getVideoData === 'function') {
      const data = player.getVideoData();
      if (data?.isLive === true || data?.isLiveContent === true) looksLive = true;
    }
  } catch { /* ignore */ }
  try {
    const info = player.playerInfo;
    if (info) {
      const ct = Number(info.currentTime);
      if (Number.isFinite(ct) && ct >= 0 && (position == null || position < 1)) {
        position = ct;
      }
      const dur = Number(info.duration);
      if (Number.isFinite(dur) && dur > 0 && !(duration > 0)) {
        duration = dur;
      }
      if (
        info.livePlaybackLatency != null
        || info.videoData?.isLive === true
        || info.videoData?.isLiveContent === true
      ) {
        looksLive = true;
      }
    }
  } catch { /* ignore */ }
  return { position, duration, playerState, looksLive };
}

/**
 * How close (seconds) current playback can be to a resume target before we
 * skip an extra seekTo — avoids rebuffering when iframe already used start=.
 */
export const RESUME_SEEK_TOLERANCE_SECONDS = 3;

export function getLocalResumeStartSeconds(youtubeVideoId) {
  const local = readLocalProgress(youtubeVideoId);
  if (!local?.position || local.position <= 0) return 0;
  return local.position;
}

export function safeSeekTo(player, positionSeconds, { force = false } = {}) {
  if (!player?.seekTo) return false;
  const position = Math.floor(Number(positionSeconds));
  if (!Number.isFinite(position) || position <= 0) return false;
  try {
    // Skip redundant seeks (e.g. iframe already loaded with start=).
    // Clip starts must force: timestamp → same-start clip is already near
    // that time, and skipping leaves playback mid-timestamp.
    const current = getPlayerPosition(player);
    if (
      !force &&
      current != null &&
      Math.abs(current - position) <= RESUME_SEEK_TOLERANCE_SECONDS
    ) {
      return true;
    }
    player.seekTo(position, true);
    // seekTo during initial autoplay often leaves the player buffering/paused;
    // nudge playback so it doesn't sit on a spinner at the resume timestamp.
    if (typeof player.playVideo === 'function') {
      player.playVideo();
    }
    return true;
  } catch {
    return false;
  }
}

/** Watch this long from a timestamp link before asking resume vs. jump. */
export const MOMENT_HOLD_WATCH_MS = 60 * 1000;

/**
 * True when a timestamp jump should freeze existing resume progress until the
 * viewer either watches a minute (then we ask) or leaves (old progress stays).
 */
export function shouldHoldMomentProgress(savedPosition, momentSeconds) {
  const saved = Math.floor(Number(savedPosition));
  const moment = Math.max(0, Math.floor(Number(momentSeconds) || 0));
  if (!Number.isFinite(saved) || saved < MIN_POSITION_SECONDS) return false;
  if (!(moment > 0)) return false;
  return Math.abs(saved - moment) >= 5;
}
